import React, { useCallback } from 'react'
import { PLAYBACK_SKIP_SECONDS } from '@/constants/video'
import { useHlsPlayerUI } from './HlsPlayerUIContext'

interface HlsSkipButtonProps {
  direction: 'forward' | 'backward'
}

const ICON_SIZE = 44

/**
 * Skip forward or backward by PLAYBACK_SKIP_SECONDS (10s).
 * Designed for the center overlay — large circular buttons
 * flanking the play/pause button with a blurred hover effect.
 */
export const HlsSkipButton: React.FC<HlsSkipButtonProps> = ({ direction }) => {
  const { controller, currentTime, duration } = useHlsPlayerUI()

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation() // Prevent the overlay click-to-play from firing
      if (!controller) return
      const offset = direction === 'forward' ? PLAYBACK_SKIP_SECONDS : -PLAYBACK_SKIP_SECONDS
      const newTime = Math.max(0, Math.min(duration, currentTime + offset))
      controller.seek(newTime)
    },
    [controller, currentTime, duration, direction],
  )

  const label =
    direction === 'forward'
      ? `Skip forward ${PLAYBACK_SKIP_SECONDS} seconds`
      : `Skip back ${PLAYBACK_SKIP_SECONDS} seconds`

  return (
    <button
      type="button"
      className="flex h-14 w-14 items-center justify-center rounded-full text-white transition-all hover:bg-white/40 hover:backdrop-blur-sm"
      onClick={handleClick}
      aria-label={label}
    >
      {direction === 'backward' ? <SkipBackwardIcon /> : <SkipForwardIcon />}
    </button>
  )
}

/**
 * Font stack matching the project's Angel Sans primary font.
 * Used inline in SVG <text> since Tailwind classes don't apply inside SVG text elements.
 */
const SVG_FONT_FAMILY = '"Angel Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

/**
 * Backward 10s icon — stroke-based circular arrow pointing left with "10" text.
 * Uses stroked paths (not filled) so strokeLinecap/strokeLinejoin produce soft, rounded ends.
 *
 * Geometry (24×24 viewBox, circle center at 12,13 radius 8):
 * - Arc: ~300° clockwise from (12,5) to (4,13)
 * - Arrowhead: chevron at the arc start pointing left
 */
function SkipBackwardIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        {/* Circular arc from top clockwise to the left (~300°) */}
        <path d="M12 5 A8 8 0 1 1 4 13" />
        {/* Arrowhead chevron pointing left, centered on the arc gap */}
        <polyline points="12.5,3 10.5,5 12.5,7" />
      </g>
      <text
        x="12"
        y="13.5"
        fill="currentColor"
        fontSize="9"
        fontWeight="500"
        fontFamily={SVG_FONT_FAMILY}
        textAnchor="middle"
        dominantBaseline="central"
      >
        10
      </text>
    </svg>
  )
}

/**
 * Forward 10s icon — stroke-based circular arrow pointing right with "10" text.
 *
 * Geometry (24×24 viewBox, circle center at 12,13 radius 8):
 * - Arc: ~300° counterclockwise from (12,5) to (20,13)
 * - Arrowhead: chevron at the arc start pointing right
 */
function SkipForwardIcon() {
  return (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none">
        {/* Circular arc from top counterclockwise to the right (~300°) */}
        <path d="M12 5 A8 8 0 1 0 20 13" />
        {/* Arrowhead chevron pointing right, centered on the arc gap */}
        <polyline points="11.5,3 13.5,5 11.5,7" />
      </g>
      <text
        x="12"
        y="13.5"
        fill="currentColor"
        fontSize="9"
        fontWeight="500"
        fontFamily={SVG_FONT_FAMILY}
        textAnchor="middle"
        dominantBaseline="central"
      >
        10
      </text>
    </svg>
  )
}
